import React, { useRef } from 'react';
import clsx from 'clsx';

// Comps
import { Image } from '@arctheme-components/elements/Image/Image';
import Video from '@project-stories/elements/Video/Video';
import InView from '@project-stories/elements/InView/InView';

// Hooks
import useVideoSource from '@arctheme-hooks/useVideoSourceQuality';

// Types
import { ILiquidMedia, ILiquidVariant } from '@src/types/product';

// Functions
import { reorderMedia } from '@project-stories/ecommerce/Product/ProductGallery/ProductGallery';

type Props = {
  media: ILiquidMedia[];
  isMobile: boolean;
  settings: any;
};

const ProductGalleryTwoColumn = ({ media, isMobile, settings }: Props) => {
  const { autoplay_video, border_radius, border_radius_mobile } =
    settings.section;

  // Refs
  const videoRefs = useRef([]);

  const initialIndex = (settings?.section?.initial_image || 1) - 1;

  const reorderedMedia = reorderMedia(media, initialIndex);

  const videoSources = reorderedMedia.map((obj) => {
    if (obj.media_type === 'video') {
      return useVideoSource(
        obj.sources,
        obj.preview_image,
        isMobile,
        settings.video_quality
      );
    }
    return null;
  });

  return (
    <div className='product-gallery relative'>
      <div className='grid grid-cols-2 lg:gap-3'>
        {reorderedMedia.map((obj, index) => {
          const videoSrc = videoSources[index]?.videoUrl || '';

          return (
            <div
              className={`w-full rounded-[${border_radius_mobile}px] lg:rounded-[${border_radius}px] overflow-hidden`}
              key={obj.id}
            >
              {obj.media_type === 'image' ? (
                <Image
                  imageUrl={obj.src}
                  alt={obj.alt}
                  classes='w-full h-full object-cover'
                  isLazy={index < 2 ? false : true}
                />
              ) : (
                <InView>
                  {(ref, isInView) => {
                    if (videoRefs.current[index] && autoplay_video) {
                      if (isInView) {
                        videoRefs.current[index].play();
                      } else {
                        videoRefs.current[index].pause();
                      }
                    }

                    return (
                      <div
                        ref={ref}
                        className='w-full'
                      >
                        <Video
                          key={obj.id}
                          ref={(ref) => (videoRefs.current[index] = ref)}
                          src={videoSrc}
                          isAutoplay={false}
                          isLazyLoad={true}
                          poster={obj.preview_image.src}
                          width={obj.preview_image.width}
                          height={obj.preview_image.height}
                          showControls={true}
                        />
                      </div>
                    );
                  }}
                </InView>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductGalleryTwoColumn;
